import {MeasureChart} from "@components/measure-chart";
import {Sensor} from "@core/model/Sensor";
import {
  Card,
  CardHeader,
  Grid,
  Stack,
  SxProps,
  Theme,
  Typography,
} from "@mui/material";
import {MeasureMap} from ".";
import {useEffect, useRef, useState} from "react";
import {Measure, Multivalue} from "@core/model/Measure";
import {renderAssetTypeIcon} from "@util";

const MonitoringGrid = ({
  selected,
  measureMap,
  limit,
  sx,
}: {
  selected: Sensor[];
  measureMap: MeasureMap;
  limit: number;
  sx?: SxProps<Theme>;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [gridHeight, setGridHeight] = useState<number | undefined>(undefined);

  useEffect(() => {
    const handleResize = () => {
      if (ref.current) {
        setGridHeight(ref.current.offsetHeight);
      }
    };

    const resizeObserver = new ResizeObserver(handleResize);

    if (ref.current) {
      resizeObserver.observe(ref.current);
    }

    handleResize();

    return () => resizeObserver.disconnect();
  }, [ref]);

  const renderChart = (
    s: Sensor,
    fullSize: boolean,
    cardHeight: number,
    measures: Measure<number | Multivalue>[] | undefined,
  ) => {
    const offset = s.type === "SENSOR_MULTIVALUE" ? 48 : 0;

    return (
      <Grid
        sx={{...sx}}
        display={"flex"}
        flex={"1 1 0"}
        size={fullSize ? 12 : {xl: 4, lg: 4, md: 4, sm: 4, xs: 12}}
        key={`grid-sensor-chart-${s.id}`}>
        <Card
          sx={{
            width: "100%",
            flex: "1 1 0",
            display: "flex",
            flexDirection: "column",
            minHeight: cardHeight,
            position: "relative",
            "& .series-selector-container": {
              position: "absolute",
              top: 12,
              right: 8,
              zIndex: 1,
            },
          }}>
          <CardHeader
            title={
              <Stack direction={"row"} alignItems={"center"} gap={1}>
                <Typography>{s.name}</Typography>{" "}
                {renderAssetTypeIcon(s.type, "small")}
              </Stack>
            }
          />
          <MeasureChart
            live
            sensor={s}
            measures={measures}
            height={cardHeight - 52 - offset}
          />
        </Card>
      </Grid>
    );
  };

  const renderSelectedCharts = (
    selected: Sensor[],
    gridHeight: number,
    measureMap: MeasureMap,
  ) => {
    const fullSize = selected.length <= 3;
    const cardHeight = fullSize ? gridHeight : gridHeight / 2 - 16;

    return selected
      .slice(0, limit)
      .map(s => renderChart(s, fullSize, cardHeight, measureMap[s.name]));
  };

  return (
    <Grid
      ref={ref}
      container
      spacing={2}
      sx={{flex: "1 1 0"}}
      overflow={"hidden"}>
      {gridHeight && renderSelectedCharts(selected, gridHeight, measureMap)}
    </Grid>
  );
};

export {MonitoringGrid};
